import * as THREE from 'three'
import Experience from '../../Experience'
import TVModel from './TVModel'

export default class TV
{
    constructor()
    {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.debug = this.experience.debug
        this.resources = this.experience.resources  // リソースへの参照を追加
        this.audioManager = this.experience.audioManager // AudioManagerへの参照を追加
        
        // 複数動画管理のプロパティを追加
        this.videoNames = ['tvVideo1', 'tvVideo2', 'tvVideo3']
        this.currentVideoIndex = 0
        this.videos = []
        this.videoTextures = []
        
        // TV電源状態管理
        this.isOn = false // 初期状態は電源OFF
        this.offTexture = null
        
        // TVModelとTVScreenのインスタンス化
        this.setupTV()

        // 2D 層（Works カルーセル）の作品切替に追従する（UI → Experience は DOM イベント経由）
        document.addEventListener('workSelected', (event) => {
            this.showVideo(event.detail.index)
        })

        // デバッグ設定
        if(this.debug.active)
        {
            this.setupDebug()
        }
    }

    handleInteract(intersectionData)
    {
        if (!this.isOn) {
            // 電源OFFの場合は電源ONにして最初の動画を再生
            this.turnOn()
        } else {
            // 電源ONの場合は次の動画に切り替える
            this.nextVideoOrTurnOff()
        }

        // ユーザーのタップ起点のみ発火（Book の byUser と同じ思想）。
        // PortfolioUI がこれを受けてカルーセルを同期し、Works へ自動スクロールする。
        // SceneDirector 起点の showVideo / turnOff では発火しない（スクロールループ防止）
        document.dispatchEvent(new CustomEvent('tvVideoChanged', {
            detail: { index: this.currentVideoIndex, isOn: this.isOn }
        }))
    }

    /**
     * 指定 index の動画を表示する連動用 API（Works カルーセル・SceneDirector から呼ばれる）。
     * 電源 OFF なら ON にする。既に同じ動画を表示中なら何もしない
     * （workSelected ↔ tvVideoChanged の相互発火を止める冪等ガード）。
     */
    showVideo(index)
    {
        if (this.videos.length === 0) return

        const clamped = ((Math.round(index) % this.videos.length) + this.videos.length) % this.videos.length
        if (this.isOn && clamped === this.currentVideoIndex) return

        if (!this.isOn) {
            // 電源ONのサウンド（turnOn と同じ。Works 進入の自動 ON でも鳴る）
            this.audioManager.playSE('on_sound_0', 0.7, 1.0)
            this.isOn = true
        } else {
            // 切替のサウンド
            this.audioManager.playSE('clickSound_0', 0.7, 1.0)
            this.video?.pause()
        }

        this.currentVideoIndex = clamped
        this.video = this.videos[clamped]
        this.videoTexture = this.videoTextures[clamped]
        this.updateScreenMaterial()

        this.playCurrentVideo()
    }

    // 現在の動画を再生する。失敗しても複数の経路で自動復旧する:
    //  1. 200ms 後に再試行（pause/play 競合の AbortError などの一時的失敗向け）
    //  2. それでも失敗したら次のユーザー操作（pointerdown = 確実なジェスチャ）で再試行
    //     （自動再生ポリシーによる NotAllowedError 向け）
    playCurrentVideo()
    {
        const video = this.video
        if (!video) return

        const stillCurrent = () => this.video === video && this.isOn

        video.play().catch((error) => {
            console.warn(`TV: play() failed (${error.name}), retrying in 200ms:`, error.message)
            setTimeout(() => {
                if (!stillCurrent()) return
                video.play().catch((error2) => {
                    console.warn(`TV: retry failed (${error2.name}), waiting for next user gesture`)
                    window.addEventListener('pointerdown', () => {
                        if (stillCurrent()) {
                            video.play().catch((err) => console.error('TV: gesture retry failed:', err))
                        }
                    }, { once: true })
                })
            }, 200)
        })
    }

    setupTV()
    {
        // TVモデルを作成
        this.tvModel = new TVModel()
        
        // TVModelにTV本体への参照を設定
        this.tvModel.setTVComponent(this)
        
        // ビデオ要素の設定
        this.setupVideo()
        // TVScreenメッシュに動画テクスチャを適用
        this.applyVideoToTVModel()
    }
    
    setupVideo()
    {
        // 複数の動画リソースを初期化
        this.videos = []
        this.videoTextures = []
        
        // 電源OFF用の黒いテクスチャを作成
        this.createOffTexture()
        
        // 各動画リソースを設定
        for (const videoName of this.videoNames) {
            const video = this.resources.items[videoName]
            if (video) {
                this.videos.push(video)
                
                // 各動画のテクスチャを作成
                // NOTE: format は既定の RGBAFormat を使う（RGBFormat は r152 以降非推奨で、
                // 環境によって動画アップロードが不安定になる経路のため使わない）
                const videoTexture = new THREE.VideoTexture(video)
                videoTexture.minFilter = THREE.LinearFilter
                videoTexture.magFilter = THREE.LinearFilter
                videoTexture.generateMipmaps = false
                videoTexture.flipY = false
                this.videoTextures.push(videoTexture)
            } else {
                console.error(`TV: Video resource "${videoName}" not found.`)
            }
        }
        
        // 初期状態では電源OFF（黒い画面）
        this.currentVideoIndex = 0
        this.video = this.videos.length > 0 ? this.videos[this.currentVideoIndex] : null
        this.videoTexture = this.offTexture // 最初は電源OFF状態
        
        if (this.videos.length === 0) {
            console.error('TV: No video resources found.')
        }
    }
    
    // 電源OFF用の黒いテクスチャを作成
    createOffTexture()
    {
        // 1x1の少しグレーがかったテクスチャを作成
        const canvas = document.createElement('canvas')
        canvas.width = 1
        canvas.height = 1
        const context = canvas.getContext('2d')
        context.fillStyle = '#020202' // 完全な黒ではなく、ダークグレー
        context.fillRect(0, 0, 1, 1)
        
        this.offTexture = new THREE.CanvasTexture(canvas)
        this.offTexture.minFilter = THREE.LinearFilter
        this.offTexture.magFilter = THREE.LinearFilter
    }
    
    setupDebug()
    {
        this.debugFolder = this.debug.ui.addFolder('TV-Controller')
        this.debugFolder.close()

        const debugObject = {
            // TV電源制御
            turnOn: () => { this.turnOn() },
            turnOff: () => { this.turnOff() },
            
            // 動画制御
            play: () => { this.play() },
            pause: () => { this.pause() },
            nextVideo: () => { this.nextVideoOrTurnOff() },
            enableEffects: () => { this.enableTVEffects() },
            disableEffects: () => { this.disableTVEffects() },
            
            // TV効果パラメータ
            noiseStrength: 0.1,
            scanlines: 1.0,
            staticNoise: 0.05,
            brightness: 1.0,
            contrast: 1.0
        }
        
        // TV電源制御ボタン
        this.debugFolder.add(debugObject, 'turnOn').name('🔌 Turn ON')
        this.debugFolder.add(debugObject, 'turnOff').name('⚫ Turn OFF')
        
        // 動画制御ボタン
        this.debugFolder.add(debugObject, 'play').name('▶️ Play Video')
        this.debugFolder.add(debugObject, 'pause').name('⏸️ Pause Video')
        this.debugFolder.add(debugObject, 'nextVideo').name('⏭️ Next Video / Turn OFF')
        this.debugFolder.add(debugObject, 'enableEffects').name('📺 Enable TV Effects')
        this.debugFolder.add(debugObject, 'disableEffects').name('🔳 Disable TV Effects')
        
        // TV効果パラメータ
        this.debugFolder.add(debugObject, 'noiseStrength', 0, 1, 0.01)
            .onChange((value) => { this.updateShaderUniform('uNoiseStrength', value) })
        this.debugFolder.add(debugObject, 'scanlines', 0, 2, 0.01)
            .onChange((value) => { this.updateShaderUniform('uScanlines', value) })
        this.debugFolder.add(debugObject, 'staticNoise', 0, 0.2, 0.001)
            .onChange((value) => { this.updateShaderUniform('uStaticNoise', value) })
        this.debugFolder.add(debugObject, 'brightness', 0, 2, 0.01)
            .onChange((value) => { this.updateShaderUniform('uBrightness', value) })
        this.debugFolder.add(debugObject, 'contrast', 0, 2, 0.01)
            .onChange((value) => { this.updateShaderUniform('uContrast', value) })
    }
    
    applyVideoToTVModel()
    {
        // 少し遅延させてTVModelが完全に読み込まれるのを待つ
        setTimeout(() => {
            const screenMesh = this.tvModel.getTVScreen()
            
            if (screenMesh) {
                // 既存のマテリアルを保存
                this.originalMaterial = screenMesh.material ? screenMesh.material.clone() : null
                // 動画テクスチャを適用したマテリアルに変更
                screenMesh.material = new THREE.MeshBasicMaterial({
                    map: this.videoTexture,
                    side: THREE.DoubleSide
                })
                
                // スクリーンメッシュの参照を保存
                this.screenMesh = screenMesh
                this.useShaderEffects = false
            } else {
                this.createFallbackScreen()
            }
        }, 100)
    }

    createFallbackScreen()
    {
        // フォールバック用のスクリーンを作成
        const geometry = new THREE.PlaneGeometry(1.6, 0.9)
        const material = new THREE.MeshBasicMaterial({
            map: this.videoTexture
        })

        this.fallbackScreen = new THREE.Mesh(geometry, material)
        this.fallbackScreen.position.set(0, 1, 0)
        this.scene.add(this.fallbackScreen)
    }

    // スクリーンマテリアルを更新するヘルパーメソッド
    updateScreenMaterial()
    {
        if (this.screenMesh) {
            if (this.useShaderEffects && this.shaderMaterial) {
                // シェーダー使用時はテクスチャユニフォームを更新
                this.shaderMaterial.uniforms.tVideo.value = this.videoTexture
            } else {
                // 通常のマテリアルを更新
                this.screenMesh.material = new THREE.MeshBasicMaterial({
                    map: this.videoTexture,
                    side: THREE.DoubleSide
                })
            }
        }

        // フォールバックスクリーンも更新
        if (this.fallbackScreen) {
            this.fallbackScreen.material.map = this.videoTexture
            this.fallbackScreen.material.needsUpdate = true
        }
    }

    // TVエフェクトを有効化
    enableTVEffects()
    {
        if (this.screenMesh) {
            // シェーダーマテリアルを作成
            this.shaderMaterial = new THREE.ShaderMaterial({
                uniforms: {
                    tVideo: { value: this.videoTexture },
                    uTime: { value: 0 },
                    uNoiseStrength: { value: 0.1 },
                    uScanlines: { value: 1.0 },
                    uStaticNoise: { value: 0.05 },
                    uBrightness: { value: 1.0 },
                    uContrast: { value: 1.0 }
                },
                vertexShader: `
                    varying vec2 vUv;
                    void main() {
                        vUv = uv;
                        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                    }
                `,
                fragmentShader: `
                    uniform sampler2D tVideo;
                    uniform float uTime;
                    uniform float uNoiseStrength;
                    uniform float uScanlines;
                    uniform float uStaticNoise;
                    uniform float uBrightness;
                    uniform float uContrast;
                    varying vec2 vUv;
                    
                    float random(vec2 st) {
                        return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
                    }
                    
                    void main() {
                        vec2 uv = vUv;
                        
                        // 元の動画テクスチャ
                        vec4 videoColor = texture2D(tVideo, uv);
                        
                        // スキャンライン効果
                        float scanline = sin(uv.y * 800.0) * 0.04 * uScanlines;
                        
                        // 静的ノイズ
                        float noise = random(uv + uTime) * uStaticNoise;
                        
                        // 色収差効果
                        float aberration = 0.002 * uNoiseStrength;
                        float r = texture2D(tVideo, uv + vec2(aberration, 0.0)).r;
                        float g = texture2D(tVideo, uv).g;
                        float b = texture2D(tVideo, uv - vec2(aberration, 0.0)).b;
                        
                        vec3 finalColor = vec3(r, g, b);
                        finalColor = ((finalColor - 0.5) * uContrast + 0.5) * uBrightness;
                        finalColor += scanline + noise;
                        
                        gl_FragColor = vec4(finalColor, videoColor.a);
                    }
                `,
                side: THREE.DoubleSide
            })
            
            this.screenMesh.material = this.shaderMaterial
            this.useShaderEffects = true
        }
    }

    // TVエフェクトを無効化
    disableTVEffects()
    {
        if (this.screenMesh) {
            this.screenMesh.material = new THREE.MeshBasicMaterial({
                map: this.videoTexture,
                side: THREE.DoubleSide
            })
            this.useShaderEffects = false
        }
    }

    // シェーダーユニフォームを更新
    updateShaderUniform(uniformName, value)
    {
        if (this.useShaderEffects && this.shaderMaterial && this.shaderMaterial.uniforms[uniformName]) {
            this.shaderMaterial.uniforms[uniformName].value = value
        }
    }

    // 音量設定
    setVolume(volume)
    {
        if (this.video) {
            this.video.volume = Math.max(0, Math.min(1, volume))
            this.video.muted = volume === 0
        }
    }

    // TV全体の位置を設定
    setPosition(x, y, z)
    {
        if (this.tvModel) {
            this.tvModel.setPosition(x, y, z)
        }
    }

    // TV全体の回転を設定
    setRotation(x, y, z)
    {
        if (this.tvModel) {
            this.tvModel.setRotation(x, y, z)
        }
    }

    // TV全体のスケールを設定
    setScale(scale)
    {
        if (this.tvModel) {
            this.tvModel.setScale(scale)
        }
    }

    // 動画再生
    play()
    {
        if (this.video) {
            this.video.play().catch(error => {
                console.error('TV: Video play failed:', error)
            })
        }
    }
    
    // 動画停止
    pause()
    {
        if (this.video) {
            this.video.pause()
        }
    }

    // 動画変更
    changeVideo(videoPath)
    {
        this.video.pause()
        this.video.src = videoPath
        this.video.load()

        this.video.addEventListener('loadeddata', () => {
            this.video.play().catch(console.error)
        }, { once: true })
    }

    // 次の動画に切り替え
    nextVideo()
    {
        if (this.videos.length === 0) {
            console.error('TV: No videos available to switch')
            return
        }

        // 現在の動画を停止
        if (this.video) {
            this.video.pause()
        }

        // 次のインデックスに切り替え
        this.currentVideoIndex = (this.currentVideoIndex + 1) % this.videos.length

        // 新しい動画とテクスチャを設定
        this.video = this.videos[this.currentVideoIndex]
        this.videoTexture = this.videoTextures[this.currentVideoIndex]

        // TVスクリーンのマテリアルを更新
        this.updateScreenMaterial()

        // 新しい動画を再生（失敗時は自動復旧）
        this.playCurrentVideo()
    }

    // 次の動画に切り替えまたは電源OFF
    nextVideoOrTurnOff()
    {
        if (!this.isOn) {
            // 電源OFFの場合は何もしない
            // console.log('TV: The TV is turned off. Please turn it on to watch videos.')
            return
        }

        // 現在の動画を停止
        if (this.video) {
            this.video.pause()
        }

        // 次のインデックスを計算
        const nextIndex = this.currentVideoIndex + 1

        if (nextIndex >= this.videos.length) {
            // 最後の動画の次は電源OFF（電源OFFのサウンドは turnOff 側で鳴る）
            this.turnOff()
            // console.log('TV: All videos finished. Turning off TV.')
        } else {
            // 次の動画に切り替え（切替のサウンドを再生）
            this.audioManager.playSE('clickSound_0', 0.7, 1.0)
            this.currentVideoIndex = nextIndex
            this.video = this.videos[this.currentVideoIndex]
            this.videoTexture = this.videoTextures[this.currentVideoIndex]

            // console.log(`TV: Switched to video ${this.currentVideoIndex + 1}/${this.videos.length}`)

            // TVスクリーンのマテリアルを更新
            this.updateScreenMaterial()

            // 新しい動画を再生（失敗時は自動復旧）
            this.playCurrentVideo()
        }
    }

    // TVの電源ON
    turnOn()
    {
        if (this.isOn) {
            // console.log('TV: TV is already on')
            return
        }
        // 電源ONのサウンドを再生
        this.audioManager.playSE('on_sound_0', 0.7, 1.0)

        this.isOn = true
        this.currentVideoIndex = 0 // 最初の動画にリセット

        // 最初の動画とテクスチャを設定
        this.video = this.videos[this.currentVideoIndex]
        this.videoTexture = this.videoTextures[this.currentVideoIndex]

        // console.log(`TV: Turned on - Playing video 1/${this.videos.length}`)

        // スクリーンマテリアルを更新
        this.updateScreenMaterial()

        // 最初の動画を再生（失敗時は自動復旧）
        this.playCurrentVideo()
    }

    // TVの電源OFF
    turnOff()
    {
        if (!this.isOn) {
            // console.log('TV: TV is already off')
            return
        }

        // 電源OFFのサウンド（タップ経由・Works 離脱の自動 OFF どちらでも鳴らす）
        this.audioManager.playSE('off_sound_0', 0.7, 1.0)

        this.isOn = false

        // 現在の動画を停止
        if (this.video) {
            this.video.pause()
        }

        // 黒い画面（電源OFF状態）に切り替え
        this.videoTexture = this.offTexture
        this.updateScreenMaterial()

        // console.log('TV: Turned off')
    }
    
    update()
    {
        // TVModelの更新（update を持たないため optional call。将来メソッドが生えたら呼ばれる）
        this.tvModel?.update?.()

        // 電源 ON 中は毎フレーム明示的にテクスチャを更新する。
        // three r177 の VideoTexture は requestVideoFrameCallback の自己再登録連鎖に依存しており、
        // 環境（ブラウザ/省電力設定/非表示 video 要素）によっては連鎖が止まり画面が凍るため、
        // rVFC に依存しないフォールバックとして needsUpdate を立てる（再生中の動画の通常コスト）
        if (this.isOn && this.videoTexture && this.videoTexture !== this.offTexture &&
            this.video && this.video.readyState >= this.video.HAVE_CURRENT_DATA) {
            this.videoTexture.needsUpdate = true
        }

        // シェーダーエフェクトの時間更新
        if (this.useShaderEffects && this.shaderMaterial) {
            this.shaderMaterial.uniforms.uTime.value += 0.016
        }
    }

    destroy()
    {
        // ビデオリソースのクリーンアップ
        if (this.video) {
            this.video.pause()
            this.video.src = ''
            this.video.load()
        }
        
        if (this.videoTexture) {
            this.videoTexture.dispose()
        }
        
        // TVModelのクリーンアップ
        if (this.tvModel) {
            this.tvModel.destroy()
        }
        
        // フォールバックスクリーンのクリーンアップ
        if (this.fallbackScreen) {
            this.scene.remove(this.fallbackScreen)
        }
        
        // デバッグフォルダのクリーンアップ
        if (this.debugFolder) {
            this.debugFolder.destroy()
        }
    }
}